import React, { useState, useEffect, Suspense, lazy } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter as Router, Routes, Route, useLocation, Navigate, useNavigate } from 'react-router-dom';
import toast, { Toaster } from 'react-hot-toast';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useCurrentUser, CurrentUserProvider } from './components/users/CurrentUserProvider';
import { MusicPlayerProvider } from './components/music_player/MusicPlayerContext';
import { LoadingProvider } from './components/create_post/LoadingContext';
import { NotificationProvider } from './components/header/NotificationContext';
import SplashScreen from './SplashScreen';
import API from './config';

import './static/css/animate.css';
import './static/css/bootstrap.min.css';
import './static/css/font-awesome.min.css';
import './static/css/line-awesome-font-awesome.min.css';
import './static/css/line-awesome.css';
import './static/css/style.css';
import './static/css/responsive.css';


const Home = lazy(() => import('./pages/Home'));
const Messages = lazy(() => import('./pages/Messages'));
const BlockedPage = lazy(() => import('./pages/BlockedPage'));
const Notifications = lazy(() => import('./pages/Notifications'));
const News = lazy(() => import('./pages/News'));
const SettingProfile = lazy(() => import('./pages/SettingProfile'));
const About = lazy(() => import('./pages/About'));
const Creator = lazy(() => import('./pages/Creator'));
const ForgotAccount = lazy(() => import('./pages/ForgotAccount'));
const SupportPage = lazy(() => import('./pages/SupportPage'));
const Profile = lazy(() => import('./pages/Profile'));
const ProfileUsers = lazy(() => import('./pages/ProfileUsers'));
const ProfileCompletionModal = lazy(() => import('./pages/ProfileCompletionModal'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const VerifyEmailNotice = lazy(() => import('./pages/VerifyEmailNotice'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const NotFound = lazy(() => import('./components/not_found/NotFound'));
const SearchPage = lazy(() => import('./pages/SearchPage'));

const Header = lazy(() => import('./components/header/Header'));
const FooterNav = lazy(() => import('./components/header/FooterNav'));
const UploadProgressBar = lazy(() => import('./components/create_post/UploadProgressBar'));
const GlobalMusicPlayer = lazy(() => import('./components/music_player/GlobalMusicPlayer'));
const MobileMenu = lazy(() => import('./components/mobile_menu/MobileMenu'));
const PostDetail = lazy(() => import('./components/post_detail/PostDetail'));
const Reels = lazy(() => import('./components/reels/Reels'));
const ResetPasswordProtectedRoute = lazy(() => import('./components/users/ResetPasswordProtectedRoute'));
const ProtectedRoute = lazy(() => import('./components/protected_route/ProtectedRoute'));
const AuthCheck = lazy(() => import('./components/protected_route/AuthCheck'));

const AppContent = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { currentUser, setCurrentUser } = useCurrentUser();
  const [showProfileModal, setShowProfileModal] = React.useState(false);
  const { t } = useTranslation();
  const [initialNavigationDone, setInitialNavigationDone] = React.useState(false);
  const [formData, setFormData] = React.useState({
    first_name: '',
    last_name: '',
    location: '',
    gender: ''
  });

  useEffect(() => {
    if (currentUser) {
      if (!currentUser.first_name || !currentUser.last_name || !currentUser.location || !currentUser.gender) {
        setFormData({
          first_name: currentUser.first_name || '',
          last_name: currentUser.last_name || '',
          location: currentUser.location || '',
          gender: currentUser.gender || ''
        });
        setShowProfileModal(true);
      }

      if (!initialNavigationDone) {
        const defaultPage = currentUser.default_page || "home";
        const pageRoutes = {
          home: "/home",
          news: "/news",
          reels: "/reels",
          messages: "/messages",
          profile: `/profile/${currentUser.id}`
        };
        const currentPath = location.pathname;
        if (currentPath === "/" || currentPath === "/home") {
          navigate(pageRoutes[defaultPage] || "/home", { replace: true });
          setInitialNavigationDone(true); 
        }
      }
    }
  }, [currentUser, navigate, location.pathname, initialNavigationDone]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.put(`${API}/users/me`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });
      setCurrentUser(response.data);
      setShowProfileModal(false);
      toast.success(t('Updating profile, successful'));
    } catch (error) {
      toast.error(t('Error updating profile. Please try again'));
    }
  };

  return (
    <NotificationProvider>
      <Header />
      <Suspense fallback={
        <>
          <div className="process-comm">
            <div className="spinner">
              <div className="bounce1"></div>
              <div className="bounce2"></div>
              <div className="bounce3"></div>
            </div>
          </div>
        </>
      }>
        <FooterNav currentUser={currentUser} />
        <UploadProgressBar />
        <Routes location={location}>
          {/* Перенаправляем "/" на "/home" */}
          <Route path="/" element={<Navigate to="/home" replace />} />

          <Route path="/blocked" element={<BlockedPage />} />
          <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
          <Route path="/messages" element={<ProtectedRoute><Messages /></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><Notifications /></ProtectedRoute>} />
          <Route path="/news" element={<ProtectedRoute><News /></ProtectedRoute>} />
          <Route path="/reels" element={<ProtectedRoute><Reels /></ProtectedRoute>} />
          <Route path="/setting_profile" element={<ProtectedRoute><SettingProfile /></ProtectedRoute>} />
          <Route path="/sign-in" element={<AuthCheck />} />
          <Route path="/about" element={<About />} />
          <Route path="/creator" element={<Creator />} />
          <Route path="*" element={<NotFound />} />
          <Route path="/verify-email-notice" element={<VerifyEmailNotice />} />
          <Route path="/verify-email/:token" element={<VerifyEmail />} />
          <Route path="/reset-password/:token" element={<ResetPasswordProtectedRoute><ResetPassword /></ResetPasswordProtectedRoute>} />
          <Route path="/forgot_account" element={<ForgotAccount />} />
          <Route path="/support_page" element={<SupportPage />} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/profile/:userId" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/profile_users" element={<ProtectedRoute><ProfileUsers /></ProtectedRoute>} />
          <Route path="/posts/:id" element={<ProtectedRoute><PostDetail /></ProtectedRoute>} />
          <Route path="/search" element={<ProtectedRoute><SearchPage /></ProtectedRoute>} />
        </Routes>
        <MobileMenu />
        <ProfileCompletionModal
          isOpen={showProfileModal}
          onRequestClose={() => setShowProfileModal(false)}
          formData={formData}
          handleChange={handleChange}
          handleSubmit={handleSubmit}
        />
      </Suspense>
    </NotificationProvider>
  );
};

const queryClient = new QueryClient();

function App() {
  const [i18nInitialized, setI18nInitialized] = useState(false);
  const [showSplash, setShowSplash] = useState(true);

  useEffect(() => {
    const initializeI18n = async () => {
      await import('./components/language_switcher/i18n').then((init) => init.default());
      setI18nInitialized(true);
    };
    initializeI18n();
  }, []);

  if (!i18nInitialized || showSplash) {
    return <SplashScreen onFinish={() => setShowSplash(false)} />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <CurrentUserProvider>
        <LoadingProvider>
          <MusicPlayerProvider>
            <Router>
              <Toaster position="top-center" reverseOrder={false} />
              <Suspense fallback={
                <>
                  <div className="process-comm">
                    <div className="spinner">
                      <div className="bounce1"></div>
                      <div className="bounce2"></div>
                      <div className="bounce3"></div>
                    </div>
                  </div>;
                </>
              }>
                <AppContent />
              </Suspense>
              <Suspense fallback={<div></div>}>
                <GlobalMusicPlayer />
              </Suspense>
            </Router>
          </MusicPlayerProvider>
        </LoadingProvider>
      </CurrentUserProvider>
    </QueryClientProvider>
  );
}

export default App;